import React, { useState } from "react";

const CALL_PROPERTIES = [
  { value: "call_end_reason", label: "Call End Reason", type: "combo", options: ["completed", "customer-ended", "assistant-ended", "silence-timed-out", "voicemail", "error"] },
  { value: "call_direction", label: "Call Direction", type: "select", options: ["inbound", "outbound"] },
  { value: "call_duration", label: "Call Duration (s)", type: "number" },
  { value: "source", label: "Source", type: "text" },
  { value: "tag", label: "Tag", type: "text" },
  { value: "metadata", label: "Metadata", type: "metadata" },
];

const PROPERTY_OPERATORS = {
  select: [
    { value: "equals", label: "=" },
    { value: "not_equals", label: "!=" },
  ],
  // Combo = suggestions + free typing (e.g. call end reason can be a vendor-specific value).
  combo: [
    { value: "equals", label: "=" },
    { value: "not_equals", label: "!=" },
    { value: "contains", label: "contains" },
  ],
  number: [
    { value: "equals", label: "=" },
    { value: "greater_than", label: ">" },
    { value: "less_than", label: "<" },
    { value: "greater_than_or_equal", label: ">=" },
    { value: "less_than_or_equal", label: "<=" },
  ],
  text: [
    { value: "equals", label: "=" },
    { value: "not_equals", label: "!=" },
    { value: "contains", label: "contains" },
    { value: "not_contains", label: "not contains" },
    { value: "has_value", label: "has value" },
    { value: "has_no_value", label: "has no value" },
  ],
  metadata: [
    { value: "equals", label: "=" },
    { value: "not_equals", label: "!=" },
    { value: "contains", label: "contains" },
    { value: "has_value", label: "has value" },
    { value: "has_no_value", label: "has no value" },
  ],
};

const NO_VALUE_OPS = ["has_value", "has_no_value"];

/**
 * Builder for a single call property condition.
 * User picks property → operator → value → clicks Apply.
 *
 * Props:
 *   onApply(condition) — called with the complete condition object
 *   compact — if true, uses smaller spacing
 *   inline  — if true, lays the controls out on one wrapping row (property | operator |
 *             value | Apply) to match the metric condition row. Default is stacked, which
 *             suits narrow sidebars.
 */
const CallPropertyBuilder = ({ onApply, compact = false, inline = false }) => {
  const [property, setProperty] = useState("");
  const [metadataKey, setMetadataKey] = useState("");
  const [operator, setOperator] = useState("equals");
  const [value, setValue] = useState("");

  const propDef = CALL_PROPERTIES.find((p) => {
    if (property.startsWith("metadata")) return p.value === "metadata";
    return p.value === property;
  });
  const propType = propDef?.type || "text";
  const operators = PROPERTY_OPERATORS[propType] || PROPERTY_OPERATORS.text;
  const hideValue = NO_VALUE_OPS.includes(operator);

  const handlePropertyChange = (val) => {
    setProperty(val);
    setOperator((PROPERTY_OPERATORS[CALL_PROPERTIES.find((p) => p.value === val)?.type || "text"]?.[0]?.value) || "equals");
    setValue("");
    setMetadataKey("");
  };

  const handleApply = () => {
    const finalProperty = property === "metadata" ? `metadata.${metadataKey}` : property;
    if (!finalProperty || (property === "metadata" && !metadataKey)) return;
    onApply({
      type: "call_property",
      property: finalProperty,
      operator,
      value: hideValue ? undefined : value,
    });
    setProperty("");
    setOperator("equals");
    setValue("");
    setMetadataKey("");
  };

  const canApply = property && (property !== "metadata" || metadataKey) && (hideValue || value);
  const sz = compact ? "text-xs" : "text-sm";
  const py = compact ? "py-1" : "py-1.5";

  // Inline lays everything on one wrapping row (auto widths + room for the dropdown
  // arrow); stacked keeps full-width controls for narrow sidebars.
  const containerCls = inline ? "flex items-center gap-2 flex-wrap" : "space-y-1.5";
  const fieldW = inline ? "" : "w-full";
  const ctrlW = inline ? "" : "flex-1";
  const selectArrow = inline ? "pr-7" : "";

  return (
    <div className={containerCls}>
      <select value={property} onChange={(e) => handlePropertyChange(e.target.value)}
        className={`${fieldW} ${selectArrow} ${sz} border border-border rounded-lg px-2 ${py} bg-card ${inline ? "min-w-[10rem]" : ""}`}>
        <option value="">Select property...</option>
        {CALL_PROPERTIES.map((p) => (
          <option key={p.value} value={p.value}>{p.label}</option>
        ))}
      </select>

      {property === "metadata" && (
        <input type="text" value={metadataKey} onChange={(e) => setMetadataKey(e.target.value)}
          placeholder="Metadata key..." className={`${fieldW} ${sz} border border-border rounded-lg px-2 ${py} bg-card ${inline ? "w-36" : ""}`} />
      )}

      {property && (
        <div className={inline ? "flex items-center gap-2" : "flex gap-1.5"}>
          <select value={operator} onChange={(e) => { setOperator(e.target.value); if (NO_VALUE_OPS.includes(e.target.value)) setValue(""); }}
            className={`${ctrlW} ${selectArrow} ${sz} border border-border rounded-lg px-2 ${py} bg-card ${inline ? "min-w-[6rem]" : ""}`}>
            {operators.map((op) => (
              <option key={op.value} value={op.value}>{op.label}</option>
            ))}
          </select>

          {!hideValue && (
            propDef?.type === "select" ? (
              <select value={value} onChange={(e) => setValue(e.target.value)}
                className={`${ctrlW} ${selectArrow} ${sz} border border-border rounded-lg px-2 ${py} bg-card ${inline ? "min-w-[8rem]" : ""}`}>
                <option value="">Select...</option>
                {propDef.options.map((o) => (
                  <option key={o} value={o}>{o.replace(/-/g, " ")}</option>
                ))}
              </select>
            ) : propDef?.type === "combo" ? (
              // Free-type input with the known values offered as suggestions.
              <>
                <input type="text" list="call-prop-combo-options"
                  value={value} onChange={(e) => setValue(e.target.value)}
                  placeholder="Select or type…"
                  className={`${ctrlW} ${sz} border border-border rounded-lg px-2 ${py} bg-card ${inline ? "w-44" : ""}`} />
                <datalist id="call-prop-combo-options">
                  {propDef.options.map((o) => (
                    <option key={o} value={o} />
                  ))}
                </datalist>
              </>
            ) : (
              <input type={propDef?.type === "number" ? "number" : "text"}
                value={value} onChange={(e) => setValue(e.target.value)}
                placeholder="Value"
                className={`${ctrlW} ${sz} border border-border rounded-lg px-2 ${py} bg-card ${inline ? "w-36" : ""}`} />
            )
          )}
        </div>
      )}

      {property && (
        <button onClick={handleApply} disabled={!canApply}
          className={`${inline ? "px-4" : "w-full"} ${sz} ${compact ? "py-1" : "py-1.5"} rounded-lg font-medium disabled:opacity-50 bg-amber-600 text-white hover:bg-amber-700`}>
          Apply
        </button>
      )}
    </div>
  );
};

export default CallPropertyBuilder;
export { CALL_PROPERTIES, PROPERTY_OPERATORS };
